import { ParsedData, ExampleRatings, FilterMode } from '../types';

const STORAGE_KEY = 'ner-comparison-tool/state/v1';
const CURRENT_SCHEMA_VERSION = 2;

export interface PersistedState {
  schemaVersion?: number;
  parsedData: ParsedData | null;
  currentFileName: string;
  scores: ExampleRatings;
  searchTerm: string;
  labelFilters: string[]; // serialize Set<string> as array
  allowAddExamples?: boolean;
  allowBatchLabelRenaming?: boolean;
  /** BQ-16: persist the disagreement filter selection across reloads.
   *  Optional so older v2 payloads (pre-BQ-16) load without migration —
   *  the schemaVersion stays at 2 since this is purely additive UI state. */
  filterMode?: FilterMode;
}

export function saveState(state: Omit<PersistedState, 'schemaVersion'>) {
  try {
    const payload = JSON.stringify({
      ...state,
      schemaVersion: CURRENT_SCHEMA_VERSION,
    });
    localStorage.setItem(STORAGE_KEY, payload);
  } catch (e) {
    // Ignore storage errors (quota/disabled)
  }
}

export function loadState(): PersistedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedState;
    // v2 cutover: discard any payload without the current schemaVersion.
    // Existing v1-shaped payloads (no version field) are silently cleared.
    if (parsed.schemaVersion !== CURRENT_SCHEMA_VERSION) {
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

export function clearState() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // noop
  }
}

/**
 * BQ-19: persist the LabelEditor batch-ops scope toggle ('all' | 'filtered')
 * under a separate storage key so we don't have to bump the main payload
 * `schemaVersion`. The toggle is session-sticky UI state; treating it as a
 * tiny independent key keeps it orthogonal to project payloads.
 */
const BATCH_SCOPE_KEY = 'ner-comparison-tool/batchScope/v1';

export type BatchScope = 'all' | 'filtered';

export function loadBatchScope(): BatchScope {
  try {
    const raw = localStorage.getItem(BATCH_SCOPE_KEY);
    if (raw === 'all' || raw === 'filtered') return raw;
    return 'all';
  } catch (e) {
    return 'all';
  }
}

export function saveBatchScope(scope: BatchScope): void {
  try {
    localStorage.setItem(BATCH_SCOPE_KEY, scope);
  } catch (e) {
    // noop
  }
}
